/**
 * Substrings of frontend errors that are NOT actionable in our code and should never become a
 * `PORTAL_FRONTEND_ERROR` ticket. They come from the user's environment (browser extensions, page
 * translators, ad-blockers), third-party scripts we don't control (Google reCAPTCHA / Analytics /
 * Adyen), or transient network/stream conditions. Matched case-insensitively against `${name} ${message}`.
 *
 * Kept deliberately explicit (not a broad `NotFoundError` catch-all) so a genuine in-app bug that
 * happens to share an error *type* still gets reported. Tune this list as new noise patterns appear.
 * Mirrors the intent of the (disabled) server-side `shouldFilterError` in `console-error-patched.ts`.
 */
const NON_ACTIONABLE_ERROR_PATTERNS = [
	// Version skew / stale chunk after a deploy — handled by a one-shot reload, never a ticket.
	'failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'importing a module script failed',
	'chunkloaderror',
	// Third-party / cross-origin scripts (reCAPTCHA, GA, Adyen…) — not our code.
	'cross-origin script load denied',
	'script error.',
	'invalid site key or not loaded in api.js',
	'recaptcha not loaded',
	'recaptcha has already been rendered',
	// Google reCAPTCHA's own script rejects with `reCAPTCHA Timeout (b)` when its challenge never
	// answers (network blip, blocked iframe); the stack lives entirely in gstatic.com/recaptcha (TEC-3453).
	'recaptcha timeout',
	// Microsoft Outlook/Office SafeLinks scans links in an embedded WebView whose injected script fails
	// with `Object Not Found Matching Id:<N>, MethodName:update, ParamCount:<N>` (TEC-3454, TEC-3327).
	'object not found matching id',
	// An injected script (bot instrumentation / extension) monkey-patches `appendChild` and tries to
	// `JSON.stringify` the node React just mounted. That always throws: React hangs a `__reactFiber$…`
	// property on every host node whose fiber points back at the element, so the cycle is guaranteed
	// (TEC-3452). Matched on the fiber key rather than on `converting circular structure to json`, so a
	// genuine circular-JSON bug in our own serialization still gets reported — we never stringify DOM.
	'__reactfiber',
	// Browser extensions / page translators (Google Translate) mutate the DOM out from under React,
	// making its next insertBefore/removeChild fail. Non-reproducible without the extension.
	"failed to execute 'insertbefore' on 'node'",
	"failed to execute 'removechild' on 'node'",
	'the node before which the new node is to be inserted is not a child of this node',
	'the object can not be found here',
	"cannot read properties of null (reading 'parentnode')",
	// Transient request/stream aborts (user navigated away, connection dropped mid-stream).
	'aborterror',
	'the operation was aborted',
	// View Transitions API rejects `.ready` with `InvalidStateError: Transition was aborted because of
	// invalid state` when a transition is superseded/aborted (re-navigation, tab hidden). Benign (TEC-3546).
	'transition was aborted',
	'the connection was closed',
	'stream lifetime exceeded',
	// Benign layout-observer feedback loop the browser reports as an error.
	'resizeobserver loop',
];

/**
 * A `RangeError: Maximum call stack size exceeded` is only ever produced by runaway
 * recursion, and its stack is the tell-tale: a tight cycle of a handful of frames
 * repeated until the engine's limit. We use that shape (many frames, very few unique)
 * to distinguish a genuine stack overflow from an unrelated error whose message merely
 * contains the phrase — so the filter below stays narrow.
 *
 * Parses both WebKit (`name@url:line:col`) and V8 (`    at name (url:line:col)`) frames.
 */
export function isStackOverflowRecursionCycle(stack: string | undefined): boolean {
	if (!stack) return false;
	const named: string[] = [];
	for (const raw of stack.split('\n')) {
		const line = raw.trim();
		if (!line) continue;
		// V8: "at fnName (...)" | "at fnName" ; WebKit: "fnName@..." ; anonymous frames have no name.
		let name: string | undefined;
		if (line.startsWith('at ')) {
			name = line.slice(3).split(' ')[0];
			if (!name || name.startsWith('(')) name = undefined; // anonymous / "at (native)"
		} else {
			const at = line.indexOf('@');
			if (at > 0) name = line.slice(0, at);
		}
		if (name) named.push(name);
	}
	// A tight recursion cycle: lots of frames, but only a couple of distinct functions.
	return named.length >= 20 && new Set(named).size <= 3;
}

/**
 * Whether a captured frontend error is worth reporting to the backend (and thus ticketing).
 * Returns false for the environmental / third-party / transient noise listed above.
 */
export function shouldReportFrontendError(name: string, message: string, stack?: string): boolean {
	// Runaway recursion overflowing the (much smaller) WebKit/iOS call stack, thrown deep inside
	// seroval's recursive serialization (a TanStack Start vendor dep) and surfaced to the global
	// error handler (TEC-3592). No application frame is involved and our own payloads are shallow
	// (verified: every campaign-route query is <=depth 9), so there is nothing to fix in our code —
	// it's the same injected-bot mechanism as TEC-3452, just via seroval instead of JSON.stringify.
	// Gated on the recursion-cycle stack shape so we only drop genuine overflows, not an unrelated
	// error that merely mentions the phrase. Caveat: a minified stack can't distinguish a vendor
	// overflow from a hypothetical in-app recursion; accepted because this is the only call-stack
	// System error on record and a real in-app recursion would be deterministic and caught pre-prod.
	if (
		name.toLowerCase().includes('rangeerror') &&
		message.toLowerCase().includes('maximum call stack size exceeded') &&
		isStackOverflowRecursionCycle(stack)
	) {
		return false;
	}

	const haystack = `${name} ${message}`.toLowerCase();
	return !NON_ACTIONABLE_ERROR_PATTERNS.some((pattern) => haystack.includes(pattern));
}
